<template lang="pug">
v-container(fluid)
  //- Barra de acciones (no se imprime)
  .report-toolbar.d-print-none
    v-btn(text color="primary" :to="`/proyeccion-comercial/${projectionId}/dashboard`")
      v-icon(left) mdi-arrow-left
      | Volver al análisis
    v-spacer
    v-btn(color="primary" :loading="exporting" @click="exportPdf")
      v-icon(left) mdi-file-pdf-box
      | Descargar PDF

  //- Documento
  #report-doc.report-doc(ref="report")
    header.rp-header
      .rp-brand
        .rp-logo Plan<span>2</span>Sell
        .rp-tagline Reporte ejecutivo comercial
      .rp-meta
        .rp-company {{ businessName }}
        .rp-sub {{ caseName }} · Generado {{ today }}

    //- KPIs
    section.rp-section
      h2.rp-h2 Resumen del caso
      .rp-kpis
        .rp-kpi
          .k-label Tamaño de mercado
          .k-value {{ (marketSize || stats.total_sales) | shortMoney }}
          .k-sub potencial anual conocido
        .rp-kpi.accent
          .k-label Venta óptima
          .k-value {{ stats.total_sales | shortMoney }}
          .k-sub mix que maximiza margen
        .rp-kpi.teal
          .k-label Margen bruto
          .k-value {{ stats.total_profit | shortMoney }}
          .k-sub {{ marginPct }}% sobre venta
        .rp-kpi(:class="netProfit >= 0 ? 'teal' : 'coral'")
          .k-label Utilidad neta
          .k-value {{ netProfit | shortMoney }}
          .k-sub tras costos fijos
        .rp-kpi
          .k-label Punto de equilibrio
          .k-value {{ breakEvenPct }}%
          .k-sub de tu plan para no perder

    //- Foco: venta vs ganancia
    section.rp-section
      h2.rp-h2 Dónde está tu ganancia
      p.rp-lead(v-if="ahaText" v-html="ahaText")
      .rp-cols
        .rp-col
          .col-head Por venta
          .rank-line(v-for="(p,i) in bySales" :key="'s'+p.name")
            span.pos {{ i+1 }}
            span.nm {{ pretty(p.name) }}
            span.vl {{ p.revenue | shortMoney }}
        .rp-col
          .col-head Por ganancia
          .rank-line(v-for="(p,i) in byMargin" :key="'m'+p.name")
            span.pos.teal {{ i+1 }}
            span.nm {{ pretty(p.name) }}
            span.vl {{ p.contribution | shortMoney }}

    //- Riesgo + equilibrio
    section.rp-section.rp-two
      .rp-box
        h3.rp-h3 Riesgo de concentración
        p.rp-box-lead(v-if="topCustomer")
          | Tu mayor cliente, <strong>{{ pretty(topCustomer.name) }}</strong>, concentra
          |  el <strong :class="concentrationClass">{{ topCustomer.pct.toFixed(0) }}%</strong> de tu margen.
        .conc(v-for="c in concentration.slice(0,4)" :key="c.name")
          .conc-h
            span {{ pretty(c.name) }}
            span.b {{ c.pct.toFixed(0) }}%
          .conc-track: .conc-fill(:style="{width: c.pct + '%'}")
      .rp-box
        h3.rp-h3 Punto de equilibrio
        p.rp-box-lead
          | Necesitas vender el <strong>{{ breakEvenPct }}%</strong> de tu plan
          | ({{ breakEvenRevenue | shortMoney }}) para no perder. Colchón de <strong>{{ safetyPct }}%</strong>.
        table.rp-mini
          thead: tr
            th Producto
            th.n Equilibrio
            th.n % de su plan
          tbody
            tr(v-for="p in breakEvenByProduct" :key="p.name")
              td {{ pretty(p.name) }}
              td.n {{ p.beRevenue | shortMoney }}
              td.n
                span.tag(:class="p.cls") {{ p.pct.toFixed(0) }}%

    //- Distribución de la venta
    section.rp-section
      h2.rp-h2 Distribución de la venta
      p.rp-lead(v-if="ventaInsight" v-html="ventaInsight")
      .rp-cols
        .rp-col
          .col-head Venta por cliente
          .bar-row(v-for="c in salesByCustomer" :key="'bc'+c.name")
            .bar-head
              span.bar-nm {{ pretty(c.name) }}
              span.bar-vl {{ c.value | shortMoney }}
            .bar-track: .bar-fill(:style="{ width: barPct(c.value, salesMax) + '%' }")
        .rp-col
          .col-head Estacionalidad del mix (margen por mes)
          .season
            .season-col(v-for="(m, i) in monthly" :key="'mo'+i")
              .season-bar-wrap: .season-bar(:style="{ height: barPct(m, monthlyMax) + '%' }")
              .season-lbl {{ monthLetters[i] }}
          p.rp-note(v-if="seasonInsight" v-html="seasonInsight")

    //- Mapa
    section.rp-section
      h2.rp-h2 Mapa de demanda por comuna
      p.rp-lead(v-if="mapaInsight" v-html="mapaInsight")
      .rp-map
        HeatMap(:filters="{}" :marketSize="marketSize")

    //- Plan de ventas detallado
    section.rp-section
      h2.rp-h2 Plan de ventas detallado
      p.rp-lead(v-if="planInsight" v-html="planInsight")
      table.rp-plan
        thead: tr
          th(v-if="!isSimpleMode") SKU
          th {{ isSimpleMode ? 'Producto o servicio' : 'Producto' }}
          th Cliente
          th.n Cantidad
          th.n Precio
          th.n Margen unit.
        tbody
          tr(v-for="(it, i) in salePlanItems" :key="'sp'+i")
            td(v-if="!isSimpleMode") {{ it.sku }}
            td {{ pretty(it.product) }}
            td {{ pretty(it.customer) }}
            td.n {{ formatInt(it.quantity) }}
            td.n {{ it.price | shortMoney }}
            td.n {{ it.margin | shortMoney }}

    //- Plan de acción
    section.rp-section
      h2.rp-h2 Recomendaciones
      ol.rp-actions
        li(v-for="(a,i) in recommendations" :key="i" v-html="a")

    footer.rp-footer
      span Plan2Sell · {{ businessName }} · {{ caseName }}
      span Documento generado automáticamente. Cifras basadas en el mix óptimo del caso.
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { fmtMoneyMM } from '@/utils/customVueFilters'
import html2pdf from 'html2pdf.js'
import HeatMap from '@/components/commercial_projection/charts/HeatMap.vue'

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

export default {
  components: { HeatMap },
  filters: {
    shortMoney(v) {
      return fmtMoneyMM(v || 0)
    },
  },
  data() {
    return {
      exporting: false,
      caseName: '',
      businessName: '',
      stats: { total_sales: 0, total_profit: 0, total_cost: 0 },
      marketSize: 0,
      products: [],
      totals: { revenue: 0, contribution: 0 },
      fixedAnnual: 0,
      concentration: [],
      salesByCustomer: [],
      monthly: new Array(12).fill(0),
      heatPoints: [],
      salePlanItems: [],
      monthLetters: ['E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
    }
  },
  computed: {
    ...mapGetters('app_config', ['currentBusiness']),
    isSimpleMode() {
      return this.currentBusiness && this.currentBusiness.catalog_mode === 'simplificado'
    },
    projectionId() {
      return this.$route.params.id
    },
    today() {
      const d = new Date()
      return `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`
    },
    marginPct() {
      return this.stats.total_sales ? Math.round((this.stats.total_profit / this.stats.total_sales) * 100) : 0
    },
    netProfit() {
      return this.stats.total_profit - this.fixedAnnual
    },
    cmr() {
      return this.stats.total_sales ? this.stats.total_profit / this.stats.total_sales : 0
    },
    breakEvenRevenue() {
      return this.cmr ? this.fixedAnnual / this.cmr : 0
    },
    breakEvenPct() {
      return this.stats.total_sales ? Math.round((this.breakEvenRevenue / this.stats.total_sales) * 100) : 0
    },
    safetyPct() {
      return this.stats.total_sales ? Math.round((1 - this.breakEvenRevenue / this.stats.total_sales) * 100) : 0
    },
    bySales() {
      return [...this.products].sort((a, b) => b.revenue - a.revenue).slice(0, 6)
    },
    byMargin() {
      return [...this.products].sort((a, b) => b.contribution - a.contribution).slice(0, 6)
    },
    ahaText() {
      const s = {}, g = {}
      this.bySales.forEach((p, i) => { s[p.name] = i + 1 })
      this.byMargin.forEach((p, i) => { g[p.name] = i + 1 })
      let worst = null
      this.products.forEach(p => {
        if (!s[p.name] || !g[p.name]) return
        const drop = g[p.name] - s[p.name]
        if (drop > 0 && (!worst || drop > worst.drop)) worst = { name: p.name, s: s[p.name], g: g[p.name], drop }
      })
      if (!worst) return 'Tu ranking de venta y de ganancia coinciden: tu foco comercial ya está alineado con tu rentabilidad.'
      return `<strong>${this.pretty(worst.name)}</strong> es tu #${worst.s} en venta, pero cae al #${worst.g} en ganancia. Vender mucho no siempre es ganar más: concentra en los productos que encabezan la columna de ganancia.`
    },
    salesMax() {
      return this.salesByCustomer.reduce((m, c) => Math.max(m, c.value), 0)
    },
    monthlyMax() {
      return this.monthly.reduce((m, v) => Math.max(m, v), 0)
    },
    topCustomer() {
      return this.concentration[0] || null
    },
    concentrationClass() {
      if (!this.topCustomer) return ''
      if (this.topCustomer.pct >= 50) return 'coral-t'
      if (this.topCustomer.pct >= 30) return 'amber-t'
      return 'teal-t'
    },
    breakEvenByProduct() {
      const totalRev = this.totals.revenue
      return this.bySales.map(p => {
        const weight = totalRev ? p.revenue / totalRev : 0
        const fixedI = this.fixedAnnual * weight
        const cmrI = p.revenue ? p.contribution / p.revenue : 0
        const beRevenue = cmrI ? fixedI / cmrI : 0
        const pct = p.revenue ? (beRevenue / p.revenue) * 100 : 0
        let cls = 'tag--ok'
        if (pct >= 85) cls = 'tag--risk'
        else if (pct >= 65) cls = 'tag--warn'
        return { name: p.name, beRevenue, pct, cls }
      })
    },
    ventaInsight() {
      if (!this.salesByCustomer.length) return ''
      const total = this.salesByCustomer.reduce((a, c) => a + c.value, 0)
      const top = this.salesByCustomer[0]
      const topPct = total ? Math.round((top.value / total) * 100) : 0
      let acc = 0, n = 0
      for (const c of this.salesByCustomer) { acc += c.value; n++; if (total && acc / total >= 0.8) break }
      return `<strong>${this.pretty(top.name)}</strong> concentra el ${topPct}% de tu venta y ${n} de tus ${this.salesByCustomer.length} clientes explican el 80%. Hacer crecer esas cuentas es tu palanca más rápida; a la vez, sumar clientes nuevos reduce tu dependencia.`
    },
    seasonInsight() {
      const m = this.monthly
      if (!m.some(v => v > 0)) return ''
      const max = Math.max(...m)
      const positives = m.filter(v => v > 0)
      const min = Math.min(...positives)
      const spread = max ? Math.round((1 - min / max) * 100) : 0
      if (spread < 8) return `Tu margen se reparte de forma pareja durante el año (variación menor al 8%): tu negocio es poco estacional, lo que facilita planificar producción y caja.`
      return `Tu mes más fuerte es <strong>${MONTHS[m.indexOf(max)]}</strong> y el más débil <strong>${MONTHS[m.indexOf(min)]}</strong>. Produce y abastécete en los meses valle para llegar con stock a los picos.`
    },
    mapaInsight() {
      if (!this.heatPoints.length) return 'Ubica a tus clientes por comuna para visualizar dónde está tu demanda y planificar rutas de reparto.'
      const total = this.heatPoints.reduce((a, p) => a + (p.weight || 0), 0)
      const top = Math.max(...this.heatPoints.map(p => p.weight || 0))
      const topPct = total ? Math.round((top / total) * 100) : 0
      return `Tu demanda llega a <strong>${this.heatPoints.length} comuna(s)</strong> y se concentra en tu comuna principal (${topPct}%). Planifica tus rutas de reparto en torno a ese núcleo y evalúa comunas vecinas para ampliar mercado.`
    },
    planInsight() {
      if (!this.salePlanItems.length) return ''
      const top = this.salePlanItems[0]
      return `Tu acuerdo más rentable es <strong>${this.pretty(top.product)} → ${this.pretty(top.customer)}</strong> (${this.money(top.quantity * top.margin)} de margen). Son ${this.salePlanItems.length} compromisos comerciales: parte cerrando los de mayor margen y usa esta tabla como meta por cliente.`
    },
    recommendations() {
      const recs = []
      const top2 = this.byMargin.slice(0, 2)
      if (top2.length === 2) {
        const share = Math.round(((top2[0].contribution + top2[1].contribution) / (this.totals.contribution || 1)) * 100)
        recs.push(`Concentra en <strong>${this.pretty(top2[0].name)}</strong> y <strong>${this.pretty(top2[1].name)}</strong>: aportan el ${share}% de tu margen.`)
      }
      const weakest = [...this.byMargin].reverse()[0]
      if (weakest) recs.push(`Revisa <strong>${this.pretty(weakest.name)}</strong>, tu producto de menor aporte al margen: evalúa subir su precio o su volumen, o discontinuarlo.`)
      if (this.topCustomer && this.topCustomer.pct >= 30) {
        recs.push(`Diversifica tu cartera: <strong>${this.pretty(this.topCustomer.name)}</strong> concentra el ${this.topCustomer.pct.toFixed(0)}% de tu margen y es tu mayor riesgo comercial.`)
      }
      recs.push(`Tu meta mínima es vender el <strong>${this.breakEvenPct}%</strong> de tu plan para cubrir costos; sobre eso, cada peso es utilidad.`)
      return recs
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getCommercialProjection', 'getOptimalMixStatistics', 'getBreakEvenBase', 'getFixedCosts', 'getOptimalMixByCustomerChart', 'getTotalProjectionChart', 'getProjectionByCutomersChart', 'listSalePlanItems', 'getHeatMapChart']),
    pretty(name) {
      if (!name) return ''
      return name.charAt(0) + name.slice(1).toLowerCase()
    },
    barPct(v, max) {
      return max ? Math.max(2, (v / max) * 100) : 0
    },
    formatInt(v) {
      return Math.round(v || 0).toLocaleString('es-CL')
    },
    money(v) {
      return fmtMoneyMM(v || 0)
    },
    async fetchAll() {
      const projection = this.projectionId
      try {
        this.businessName = (JSON.parse(localStorage.current_business || '{}').name) || 'Tu empresa'
      } catch (e) { this.businessName = 'Tu empresa' }

      const [caseRes, statsRes, baseRes, fixedRes, concRes, potRes, salesCustRes, planRes, heatRes] = await Promise.all([
        this.getCommercialProjection(projection),
        this.getOptimalMixStatistics({ projection }),
        this.getBreakEvenBase({ projection, base: 'optimal' }),
        this.getFixedCosts(),
        this.getOptimalMixByCustomerChart({ projection }),
        this.getTotalProjectionChart({ projection }),
        this.getProjectionByCutomersChart({ projection }),
        this.listSalePlanItems({ projection, page: 1, page_size: 100 }),
        this.getHeatMapChart({ projection }),
      ])

      if (!caseRes.error) this.caseName = caseRes.data.name
      if (!statsRes.error) this.stats = statsRes.data
      if (!potRes.error && potRes.data) this.marketSize = potRes.data.total || 0
      if (!baseRes.error) {
        this.products = (baseRes.data.products || []).map(p => ({
          name: p.product_name,
          revenue: p.revenue,
          contribution: p.contribution,
          quantity: p.quantity,
        }))
        this.totals = baseRes.data.totals || { revenue: 0, contribution: 0 }
      }
      if (!fixedRes.error && fixedRes.data) {
        this.fixedAnnual = fixedRes.data.reduce((a, c) => a + (c.monthly_amount || 0), 0) * 12
      }
      if (!concRes.error && concRes.data && concRes.data.datasets) {
        const custs = concRes.data.datasets.map(d => ({ name: d.label, value: (d.data || []).reduce((a, v) => a + (v || 0), 0) }))
        const total = custs.reduce((a, c) => a + c.value, 0)
        this.concentration = custs.filter(c => c.value > 0).sort((a, b) => b.value - a.value).map(c => ({ ...c, pct: total ? (c.value / total) * 100 : 0 }))
        // Estacionalidad: margen total por mes (suma de todos los clientes)
        const monthly = new Array(12).fill(0)
        concRes.data.datasets.forEach(d => (d.data || []).forEach((v, m) => { monthly[m] += (v || 0) }))
        this.monthly = monthly
      }

      if (!salesCustRes.error && salesCustRes.data) {
        const labels = salesCustRes.data.labels || []
        const values = (salesCustRes.data.datasets && salesCustRes.data.datasets[0]) || []
        this.salesByCustomer = labels.map((name, i) => ({ name, value: values[i] || 0 }))
          .filter(c => c.value > 0).sort((a, b) => b.value - a.value)
      }

      if (!heatRes.error && heatRes.data) {
        this.heatPoints = Array.isArray(heatRes.data) ? heatRes.data : (heatRes.data.results || [])
      }

      if (!planRes.error && planRes.data) {
        const arr = planRes.data.results || planRes.data
        this.salePlanItems = (arr || []).map(it => ({
          sku: it.product_sku,
          product: it.product_name,
          customer: it.customer_name,
          quantity: it.total_quantity,
          price: it.price,
          margin: it.profit,
        })).sort((a, b) => (b.quantity * b.margin) - (a.quantity * a.margin))
      }
    },
    async exportPdf() {
      this.exporting = true
      const opt = {
        margin: [8, 8, 8, 8],
        filename: `Reporte ${this.businessName} - ${this.caseName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 3, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], avoid: ['.rp-section', '.rp-box', 'tr'] },
      }
      try {
        await html2pdf().set(opt).from(this.$refs.report).save()
      } finally {
        this.exporting = false
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>

<style scoped>
.report-toolbar { display: flex; align-items: center; max-width: 820px; margin: 0 auto 16px; }

.report-doc {
  max-width: 820px;
  margin: 0 auto;
  background: #fff;
  color: #1B2E38;
  font-family: "Poppins", sans-serif;
  padding: 40px 44px;
  border-radius: 14px;
  box-shadow: 0 2px 18px rgba(27,46,56,.10);
}

/* Header */
.rp-header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #5575DF; padding-bottom: 16px; margin-bottom: 26px; }
.rp-logo { font-size: 26px; font-weight: 700; letter-spacing: -.02em; color: #1B2E38; }
.rp-logo span { color: #FF7D6A; }
.rp-tagline { font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: #8A99A3; font-weight: 600; margin-top: 2px; }
.rp-meta { text-align: right; }
.rp-company { font-size: 18px; font-weight: 600; }
.rp-sub { font-size: 12.5px; color: #8A99A3; }

.rp-section { margin-bottom: 26px; }
.rp-h2 { font-size: 15px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; color: #5575DF; margin: 0 0 14px; }
.rp-h3 { font-size: 14px; font-weight: 700; color: #1B2E38; margin: 0 0 8px; }
.rp-lead { font-size: 14px; line-height: 1.6; color: #4A5A66; margin: 0 0 14px; }

/* KPIs */
.rp-kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
.rp-kpi { border: 1px solid #ECEDF0; border-radius: 12px; padding: 12px; }
.rp-kpi.accent { background: #EAEEFC; border-color: #D6DEF7; }
.rp-kpi.teal { background: #E1F6F1; border-color: #C5EDE4; }
.rp-kpi.coral { background: #FCE7E1; border-color: #F6D3C9; }
.k-label { font-size: 10.5px; color: #8A99A3; text-transform: uppercase; letter-spacing: .04em; font-weight: 600; }
.k-value { font-size: 19px; font-weight: 700; color: #1B2E38; font-variant-numeric: tabular-nums; margin-top: 4px; }
.k-sub { font-size: 10.5px; color: #8A99A3; margin-top: 2px; }

/* rankings */
.rp-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.col-head { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; color: #8A99A3; border-bottom: 1px solid #ECEDF0; padding-bottom: 6px; margin-bottom: 6px; }
.rank-line { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid #F5F6F9; font-size: 13px; }
.rank-line .pos { width: 22px; height: 22px; border-radius: 50%; background: #EAEEFC; color: #5575DF; font-weight: 700; font-size: 12px; display: flex; align-items: center; justify-content: center; flex: none; }
.rank-line .pos.teal { background: #E1F6F1; color: #0FA890; }
.rank-line .nm { flex: 1 1 auto; }
.rank-line .vl { font-variant-numeric: tabular-nums; color: #4A5A66; }

/* two boxes */
.rp-two { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.rp-box-lead { font-size: 13px; line-height: 1.55; color: #4A5A66; margin: 0 0 12px; }
.coral-t { color: #E0533C; } .amber-t { color: #C9821B; } .teal-t { color: #0FA890; }
.conc { margin-bottom: 9px; }
.conc-h { display: flex; justify-content: space-between; font-size: 12.5px; margin-bottom: 3px; }
.conc-h .b { font-weight: 700; }
.conc-track { height: 7px; background: #F0F2F7; border-radius: 5px; overflow: hidden; }
.conc-fill { height: 100%; background: #5575DF; border-radius: 5px; }

.rp-mini { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.rp-mini th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #8A99A3; padding: 5px 6px; border-bottom: 1px solid #ECEDF0; }
.rp-mini td { padding: 6px; border-bottom: 1px solid #F5F6F9; }
.rp-mini .n { text-align: right; font-variant-numeric: tabular-nums; }
.tag { font-weight: 700; padding: 1px 7px; border-radius: 999px; font-size: 11.5px; }
.tag--ok { color: #0FA890; background: #E1F6F1; }
.tag--warn { color: #C9821B; background: #FBF0DC; }
.tag--risk { color: #E0533C; background: #FCE7E1; }

/* actions */
.rp-actions { margin: 0; padding-left: 20px; }
.rp-actions li { font-size: 13.5px; line-height: 1.6; color: #1B2E38; margin-bottom: 8px; }

/* venta por cliente (barras) */
.bar-row { margin-bottom: 10px; }
.bar-head { display: flex; justify-content: space-between; font-size: 12.5px; margin-bottom: 3px; }
.bar-head .bar-vl { font-variant-numeric: tabular-nums; color: #4A5A66; }
.bar-track { height: 8px; background: #F0F2F7; border-radius: 5px; overflow: hidden; }
.bar-fill { height: 100%; background: #5575DF; border-radius: 5px; }

/* estacionalidad */
.season { display: flex; align-items: flex-end; gap: 6px; height: 130px; padding-top: 8px; }
.season-col { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; }
.season-bar-wrap { flex: 1; width: 100%; display: flex; align-items: flex-end; }
.season-bar { width: 100%; background: #19BFA7; border-radius: 4px 4px 0 0; min-height: 2px; }
.season-lbl { font-size: 10px; color: #8A99A3; margin-top: 4px; }
.rp-note { font-size: 12px; color: #4A5A66; line-height: 1.5; margin: 12px 0 0; }

/* mapa */
.rp-map { border: 1px solid #ECEDF0; border-radius: 12px; overflow: hidden; }
.rp-map >>> #leafletmap { height: 300px !important; min-height: 300px !important; }

/* plan detallado */
.rp-plan { width: 100%; border-collapse: collapse; font-size: 12px; }
.rp-plan th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #8A99A3; padding: 6px 8px; border-bottom: 1px solid #ECEDF0; background: #FAFAFB; }
.rp-plan td { padding: 6px 8px; border-bottom: 1px solid #F5F6F9; }
.rp-plan .n { text-align: right; font-variant-numeric: tabular-nums; }

.rp-footer { display: flex; justify-content: space-between; border-top: 1px solid #ECEDF0; margin-top: 30px; padding-top: 12px; font-size: 10.5px; color: #B9C4CC; }

@media (max-width: 640px) {
  .rp-kpis { grid-template-columns: 1fr 1fr; }
  .rp-cols, .rp-two { grid-template-columns: 1fr; }
}
</style>
